/**
 * Locale-prefixed URLs for the public site and the docs.
 *
 * English pages keep their unprefixed URLs (/docs/...); every other locale
 * lives under its own prefix (/ja/docs/...), so each language version has a
 * URL of its own that search engines can index. App pages (sign-in,
 * dashboard, admin) are never prefixed.
 */

import { sitePaths, SINGLE_LOCALE_PAGES } from './site-routes';

export const LOCALES = ['en', 'zh', 'ja', 'ko', 'es', 'fr', 'ar', 'pt', 'ru', 'de', 'uk', 'it'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';
export const PREFIXED_LOCALES = LOCALES.filter((l) => l !== DEFAULT_LOCALE) as Exclude<Locale, 'en'>[];

export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value);
}

const LOCALIZED_PAGES: ReadonlySet<string> = new Set(sitePaths().filter((p) => !SINGLE_LOCALE_PAGES.has(p)));

/** Whether an unprefixed path has a version in every locale. */
export function isLocalizedPage(path: string): boolean {
  return LOCALIZED_PAGES.has(path);
}

/** Splits a leading locale prefix off a pathname. English is never a prefix. */
export function splitLocalePath(pathname: string): { locale: Locale | null; path: string } {
  const match = /^\/([a-z]{2})(?=\/|$)(.*)$/.exec(pathname);
  if (match && match[1] !== DEFAULT_LOCALE && isLocale(match[1])) {
    const rest = match[2].replace(/\/+$/, '');
    return { locale: match[1], path: rest || '/' };
  }
  return { locale: null, path: pathname };
}

/**
 * The URL of a site page in the given locale. Anything that is not an
 * internal page with per-locale versions (app routes, external URLs, bare
 * anchors, mailto:) comes back unchanged. Hash and query are kept.
 */
export function localizePath(to: string, locale: Locale): string {
  if (!to.startsWith('/') || to.startsWith('//')) return to;
  const split = /^([^?#]*)(.*)$/.exec(to)!;
  const { path } = splitLocalePath(split[1] || '/');
  if (!isLocalizedPage(path)) return to;
  if (locale === DEFAULT_LOCALE) return path + split[2];
  return `/${locale}${path === '/' ? '' : path}${split[2]}`;
}

/** Rewrites the internal href="/..." links inside a translated HTML string. */
export function localizeHtmlLinks(html: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return html;
  return html.replace(/href="(\/[^"]*)"/g, (_, href: string) => `href="${localizePath(href, locale)}"`);
}
